import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation, PermissionNavigationItem } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap, combineLatest, BehaviorSubject } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _authService = inject(AuthService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);
    private _originalNavigation: BehaviorSubject<Navigation | null> = new BehaviorSubject<Navigation | null>(null);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Constructor
     */
    constructor() {
        // Subscribe to permissions and original navigation changes
        combineLatest([
            this._originalNavigation,
            this._authService.permissions$
        ]).subscribe(([navigation, permissions]) => {
            console.log('NavigationService: CombineLatest fired', { hasNavigation: !!navigation, permissions });
            if (navigation) {
                const filtered = this._filterNavigation(navigation);
                console.log('NavigationService: Navigation filtered', filtered);
                this._navigation.next(filtered);
            }
        });
    }

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                this._originalNavigation.next(navigation);
            })
        );
    }

    /**
     * Filter navigation based on user permissions
     */
    private _filterNavigation(navigation: Navigation): Navigation {
        // Clone navigation to avoid modifying the original
        const nav = cloneDeep(navigation);

        return {
            compact: this._filterItems(nav.compact),
            default: this._filterItems(nav.default),
            futuristic: this._filterItems(nav.futuristic),
            horizontal: this._filterItems(nav.horizontal)
        };
    }

    /**
     * Filter navigation items recursively
     */
    private _filterItems(items: PermissionNavigationItem[]): PermissionNavigationItem[] {
        return items.filter(item => {
            // Check permissions for the item
            if (item.permissions && item.permissions.length > 0) {
                const hasPermission = this._authService.hasAnyPermission(item.permissions);
                console.log(`NavigationService: Checking item ${item.id}`, { required: item.permissions, hasAccess: hasPermission });
                if (!hasPermission) {
                    return false;
                }
            }

            // Check children
            if (item.children) {
                item.children = this._filterItems(item.children);
                // If item has no link and all children are removed, remove the item
                if (!item.link && item.children.length === 0) {
                    console.log(`NavigationService: Removing parent ${item.id} because children are empty`);
                    return false;
                }
            }

            return true;
        });
    }
}
